import Loader from 'components/Loader';
import { useState } from 'react';
import { useParams } from 'react-router';
import useSecrets from 'api/secrets/useSecrets';
import SecretItem from './SecretItem';
import SecretAdd from './SecretAdd';
import SecretEdit from './SecretEdit';
import { useSearchParams } from 'react-router-dom';
import { useKeyboard } from 'hooks/useKeyboard';
import { SettingsSection } from 'components/settingsHub/SettingsSection';
import { Scope } from 'components/settingsHub/ScopeFilter';
import { AddEntityCard } from 'components/settingsHub/EntityCard';
import { EntityGrid } from 'components/settingsHub/settingsEntities.styles';
import { SettingsDetailPanel } from 'components/settingsHub/SettingsDetailPanel';

type Props = {
  projectId?: string | null;
};

const SecretsView = ({ projectId = null }: Props) => {
  const { projectId: routeProjectId } = useParams();
  const actualProjectId = projectId ?? routeProjectId;
  const [scope, setScope] = useState<Scope>(actualProjectId ? 'project' : 'global');
  const includeGlobal = scope === 'all' || scope === 'global';

  const { secrets, isLoading } = useSecrets(scope === 'global' ? null : actualProjectId, includeGlobal);
  const [addSecretFormOpen, setAddSecretFormOpen] = useState(false);
  const [editSecretId, setEditSecretId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  useKeyboard({
    keyDown: (e) => {
      if (e.key === 'Escape' && searchParams.get('config')) {
        if (editSecretId) {
          setEditSecretId(null);
        } else if (addSecretFormOpen) {
          setAddSecretFormOpen(false);
        } else {
          setSearchParams({});
        }
      }
    },
  });

  if (isLoading) return <Loader />;

  const query = search.trim().toLowerCase();
  const filtered = (secrets ?? []).filter(
    (s) => !query || s.name.toLowerCase().includes(query) || (s.description ?? '').toLowerCase().includes(query),
  );

  return (
    <>
      <SettingsSection
        showScope={!!actualProjectId}
        scope={scope}
        onScopeChange={setScope}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search secrets…"
        count={filtered.length}
        countNoun="secrets"
      >
        <EntityGrid minTile={220}>
          <AddEntityCard label="Add New Secret" onClick={() => setAddSecretFormOpen(true)} minHeight={208} />
          {filtered.map((secret) => (
            <SecretItem key={secret.id} secret={secret} onSelect={() => setEditSecretId(secret.id)} />
          ))}
        </EntityGrid>
      </SettingsSection>

      {addSecretFormOpen && (
        <SettingsDetailPanel onClose={() => setAddSecretFormOpen(false)}>
          <SecretAdd
            projectId={actualProjectId ?? ''}
            onCancel={() => setAddSecretFormOpen(false)}
            onSubmit={() => setAddSecretFormOpen(false)}
          />
        </SettingsDetailPanel>
      )}

      {editSecretId && (
        <SettingsDetailPanel onClose={() => setEditSecretId(null)}>
          <SecretEdit
            secretId={editSecretId}
            onCancel={() => setEditSecretId(null)}
            onSubmit={() => setEditSecretId(null)}
          />
        </SettingsDetailPanel>
      )}
    </>
  );
};

export default SecretsView;
